import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { HABIT_ICONS, HABIT_COLORS } from '../lib/constants';
import { DAYS_OF_WEEK } from '../lib/avatars';
import { updateHabit } from '../lib/storage';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';

const EditHabitModal = ({ open, onClose, habit, onHabitUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    icon: 'Circle',
    category: '',
    color: HABIT_COLORS[0],
    scheduledDays: [],
  });

  useEffect(() => {
    if (habit) {
      setFormData({
        name: habit.name || '',
        icon: habit.icon || 'Circle',
        category: habit.category || '',
        color: habit.color || HABIT_COLORS[0],
        scheduledDays: habit.scheduledDays || [],
      });
    }
  }, [habit]);

  const toggleDay = (day) => {
    setFormData(prev => {
      const days = prev.scheduledDays || [];
      if (days.includes(day)) {
        return { ...prev, scheduledDays: days.filter(d => d !== day) };
      } else {
        return { ...prev, scheduledDays: [...days, day] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a habit name');
      return;
    }

    if (loading) return;

    try {
      setLoading(true);
      // If all days selected or none selected, set to null (daily)
      const scheduledDays = formData.scheduledDays.length === 0 || formData.scheduledDays.length === 7 
        ? null 
        : formData.scheduledDays;
      
      const updatedHabitData = await updateHabit(habit.id, {
        ...formData,
        scheduledDays,
      });
      toast.success(`Updated "${formData.name}" habit!`);
      onClose();
      // Call onHabitUpdated after closing to ensure modal closes first
      setTimeout(() => onHabitUpdated(updatedHabitData), 0);
    } catch (error) {
      console.error('Error updating habit:', error);
      toast.error('Failed to update habit');
    } finally {
      setLoading(false);
    }
  };

  if (!habit) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-2xl max-h-[90vh] overflow-hidden gradient-card"
        data-testid="edit-habit-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl gradient-text">Edit Habit</DialogTitle>
          <DialogDescription>
            Update your habit details below.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[450px] pr-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-habit-name">Habit Name *</Label>
              <Input
                id="edit-habit-name"
                data-testid="edit-habit-name-input"
                placeholder="e.g., Practice Piano"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-habit-category">Category (Optional)</Label>
              <Input
                id="edit-habit-category"
                data-testid="edit-habit-category-input"
                placeholder="e.g., Music, Health, Learning"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>

            {/* Schedule Days */}
            <div className="space-y-2">
              <Label>Schedule (leave empty for daily)</Label>
              <div className="flex gap-2 flex-wrap">
                {DAYS_OF_WEEK.map((day) => {
                  const isSelected = formData.scheduledDays?.includes(day.short);
                  return (
                    <button
                      key={day.short}
                      type="button"
                      data-testid={`edit-day-${day.short}`}
                      onClick={() => toggleDay(day.short)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isSelected
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {day.short}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                {formData.scheduledDays?.length === 0 
                  ? 'Daily habit' 
                  : formData.scheduledDays?.length === 7 
                    ? 'Daily habit'
                    : `Every ${formData.scheduledDays?.join(', ')}`}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-10 gap-2 max-h-48 overflow-y-auto p-2 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                {HABIT_ICONS.map((iconName) => {
                  const IconComponent = Icons[iconName] || Icons.Circle;
                  const isSelected = formData.icon === iconName;
                  return (
                    <button
                      key={iconName}
                      type="button"
                      data-testid={`edit-icon-${iconName}`}
                      onClick={() => setFormData({ ...formData, icon: iconName })}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                        isSelected
                          ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg scale-110'
                          : 'bg-white dark:bg-slate-800 hover:bg-gradient-to-br hover:from-blue-500/20 hover:to-purple-600/20 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
                      }`}
                      title={iconName}
                    >
                      <IconComponent className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {HABIT_COLORS.map((color) => {
                  const isSelected = formData.color === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      data-testid={`edit-color-${color}`}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-lg transition-all duration-200 ${
                        isSelected ? 'ring-4 ring-blue-500 ring-offset-2 scale-110' : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Select color ${color}`}
                    />
                  );
                })}
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                data-testid="cancel-edit-button"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="update-habit-button"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover-glow"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Habit'}
              </Button>
            </DialogFooter>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default EditHabitModal;
